import { ClientId } from "@sp-crm/core";
import { trpc, trpcClient } from "components/rpc/trpc";
import { fancyAlert } from "components/ui/fancy-alert";
import { useInvalidateEntityNotes } from "hooks/use-invalidate-entity-notes";
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { useTenantSettings } from "store/selectors/settings";
import { useQuery } from "@tanstack/react-query";

type TimerState = "idle" | "running" | "stopped";

interface TimerContextValue {
    timerState: TimerState;
    elapsedSeconds: number;
    clientId: ClientId | null;
    clientName: string;
    startTimer: (clientId: ClientId, clientName: string) => Promise<void>;
    stopTimer: () => void;
    resetTimer: () => void;
    saveEntry: (note: string) => Promise<void>;
}

const TimerContext = createContext<TimerContextValue | null>(null);

export function formatElapsed(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [timerState, setTimerState] = useState<TimerState>("idle");
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [clientId, setClientId] = useState<ClientId | null>(null);
    const [clientName, setClientName] = useState("");
    const startTimeRef = useRef<Date | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const elapsedSecondsRef = useRef(elapsedSeconds);
    elapsedSecondsRef.current = elapsedSeconds;
    const hydratedRef = useRef(false);

    const trackTimeFeature = useFeature("trackTime");
    const { settings } = useTenantSettings();
    const isTimeTrackingEnabled = trackTimeFeature && settings.trackTimeEnabled;
    const regionId = useRegionId();

    const invalidateEntityNotes = useInvalidateEntityNotes();
    const trpcHooks = trpc.useTRPC();
    const { data: serverTimer } = useQuery(
        trpcHooks.activeTimers.getCurrent.queryOptions(
            { regionId: regionId! },
            {
                enabled: isTimeTrackingEnabled && !!regionId,
            },
        ),
    );

    useEffect(() => {
        if (hydratedRef.current) return;
        if (!serverTimer) return;

        hydratedRef.current = true;
        startTimeRef.current = new Date(serverTimer.startedAt);
        setClientId(serverTimer.clientId);
        setClientName(serverTimer.clientName);
        setTimerState("running");
    }, [serverTimer]);

    const clearInterval_ = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const startInterval = useCallback(
        (start: Date) => {
            clearInterval_();
            intervalRef.current = setInterval(() => {
                const now = new Date();
                const diff = Math.floor((now.getTime() - start.getTime()) / 1000);
                setElapsedSeconds(diff);
            }, 1000);
        },
        [clearInterval_],
    );

    const startTimer = useCallback(
        async (id: ClientId, name: string) => {
            try {
                await trpcClient.activeTimers.start.mutate({ clientId: id });
            } catch {
                await fancyAlert(
                    "Timer error",
                    "Failed to start timer. Please try again.",
                );
                return;
            }
            const now = new Date();
            startTimeRef.current = now;
            setClientId(id);
            setClientName(name);
            setElapsedSeconds(0);
            setTimerState("running");
            startInterval(now);
            handleEvent("timer-started");
        },
        [startInterval],
    );

    const stopTimer = useCallback(() => {
        clearInterval_();
        setTimerState("stopped");
        handleEvent("timer-stopped");
    }, [clearInterval_]);

    const teardownTimer = useCallback(() => {
        clearInterval_();
        startTimeRef.current = null;
        setClientId(null);
        setClientName("");
        setElapsedSeconds(0);
        setTimerState("idle");
        if (regionId) {
            trpcClient.activeTimers.stop.mutate({ regionId }).catch(() => {
                fancyAlert(
                    "Timer error",
                    "Failed to clear timer on server. It may reappear on next page load.",
                );
            });
        }
    }, [clearInterval_, regionId]);

    const resetTimer = useCallback(() => {
        teardownTimer();
    }, [teardownTimer]);

    const saveEntry = useCallback(
        async (note: string) => {
            if (!startTimeRef.current || !clientId) return;

            const endTime = new Date();
            const durationMinutes = Math.max(
                1,
                Math.ceil(elapsedSecondsRef.current / 60),
            );

            await trpcClient.timeEntries.create.mutate({
                clientId,
                startTime: startTimeRef.current.toISOString(),
                endTime: endTime.toISOString(),
                durationMinutes,
                note: note.trim(),
                isManual: false,
            });

            invalidateEntityNotes();
            teardownTimer();
        },
        [clientId, teardownTimer, invalidateEntityNotes],
    );

    useEffect(() => {
        if (timerState === "running" && startTimeRef.current) {
            startInterval(startTimeRef.current);
        }
        return clearInterval_;
    }, [timerState, startInterval, clearInterval_]);

    useEffect(() => {
        if (!isTimeTrackingEnabled && timerState !== "idle") {
            clearInterval_();
            startTimeRef.current = null;
            setClientId(null);
            setClientName("");
            setElapsedSeconds(0);
            setTimerState("idle");
        }
    }, [isTimeTrackingEnabled, timerState, clearInterval_]);

    const value = useMemo<TimerContextValue>(
        () => ({
            timerState,
            elapsedSeconds,
            clientId,
            clientName,
            startTimer,
            stopTimer,
            resetTimer,
            saveEntry,
        }),
        [
            timerState,
            elapsedSeconds,
            clientId,
            clientName,
            startTimer,
            stopTimer,
            resetTimer,
            saveEntry,
        ],
    );

    return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
};

export function useTimer(): TimerContextValue {
    const ctx = useContext(TimerContext);
    if (!ctx) {
        throw new Error("useTimer must be used within a TimerProvider");
    }
    return ctx;
}
